import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Virtuoso } from 'react-virtuoso';
import {
  Box,
  Backdrop,
  ClickAwayListener,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItem,
  ListItemText,
  Paper,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { X as CloseIcon, Zap } from 'react-feather';
import startCase from 'lodash/startCase';
import moment from 'moment';
import { Fields } from '@pv/common/components';

const adminSearchQuery = gql`
  query AdminSearch($query: String, $first: Int, $after: String) {
    adminSearch(query: $query, first: $first, after: $after) {
      nodes {
        __typename
        ... on Organization {
          id
          name
          discardedAt
        }
        ... on Venue {
          id
          name
          discardedAt
        }
        ... on User {
          id
          firstName
          lastName
          email
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      query
    }
  }
`;

const ResultText = ({ discarded, children }) => {
  return (
    <Typography
      variant="body1"
      color={discarded ? 'textSecondary' : 'textPrimary'}
    >
      {discarded && <span> (deleted) </span>}
      {children}
    </Typography>
  );
};

const OrganizationResult = ({ node }) => {
  return (
    <ListItem
      component="a"
      className="organization-search-result"
      href={`/admin/organizations/${node.id}`}
    >
      <ResultText discarded={node.discardedAt}>
        Organization: {node.name}
      </ResultText>
    </ListItem>
  );
};

const VenueResult = ({ node }) => {
  return (
    <ListItem
      component="a"
      className="venue-search-result"
      href={`/admin/venues/${node.id}`}
    >
      <ResultText discarded={node.discardedAt}>Venue: {node.name}</ResultText>
    </ListItem>
  );
};

const UserResult = ({ node }) => {
  return (
    <ListItem
      component="a"
      className="user-search-result"
      href={`/admin/users/${node.id}`}
    >
      <ResultText discarded={node.discardedAt}>
        User: {node.firstName} {node.lastName} ({node.email})
      </ResultText>
    </ListItem>
  );
};

const SearchableNode = ({ node, onClose }) => {
  if (!node) return null;

  const __typename = node.__typename;

  if (__typename === 'Organization') {
    return <OrganizationResult node={node} />;
  }
  if (__typename === 'Venue') {
    return <VenueResult node={node} />;
  }
  if (__typename === 'User') {
    return <UserResult node={node} />;
  }
  return null;
};

export const AdminMultiSearch = ({ open, onClose }) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [query, setQuery] = useState('');
  const [noMatches, setNoMatches] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const variables = { query, first: 20 };
  const skip = !query;
  const { data, loading, fetchMore } = useQuery(adminSearchQuery, {
    variables,
    skip,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const adminSearch = data?.adminSearch;
  const totalCount = adminSearch?.totalCount || 0;
  const pageInfo = adminSearch?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const endCursor = pageInfo?.endCursor;

  const sortedNodes = useMemo(() => {
    const nodes = adminSearch?.nodes || [];
    if (!nodes) return [];
    const filteredNodes = nodes.filter((node) => node != null);
    const nonArchived = filteredNodes.filter((node) => !node.discardedAt);
    const archived = filteredNodes.filter((node) => !!node.discardedAt);
    return [...nonArchived, ...archived];
  }, [adminSearch?.nodes]);

  const matchingDocs = totalCount > 0;

  const loadMore = useCallback(async () => {
    if (!hasNextPage || loading || isLoadingMore || !endCursor || !query)
      return;

    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          after: endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.adminSearch) return prev;

          return {
            adminSearch: {
              ...fetchMoreResult.adminSearch,
              nodes: [
                ...(prev.adminSearch?.nodes || []),
                ...(fetchMoreResult.adminSearch.nodes || []),
              ],
            },
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasNextPage, loading, isLoadingMore, endCursor, fetchMore, query]);

  useEffect(() => {
    if (!loading && query) {
      if (matchingDocs) {
        setNoMatches(false);
      } else {
        setNoMatches(true);
      }
    }
  }, [loading, matchingDocs, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setIsLoadingMore(false);
    }
  }, [open]);

  // Reset loading state when query changes
  useEffect(() => {
    setIsLoadingMore(false);
  }, [query]);

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: theme.zIndex.modal - 1,
      }}
    >
      <Popover
        sx={{
          zIndex: theme.zIndex.modal + 100,
        }}
        open={open}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: smallScreen ? 0 : 200,
          left: window.innerWidth / 2,
        }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <ClickAwayListener onClickAway={onClose}>
          <Paper
            sx={{
              width: '100%',
              overflowY: 'hidden',
              [theme.breakpoints.up('md')]: {
                width: 700,
              },
            }}
            elevation={3}
          >
            <Box
              sx={{
                padding: theme.spacing(2),
              }}
            >
              <Fields.PvTextField
                autoFocus
                placeholder="Search Anything"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disableUnderline
                variant="standard"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Zap />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={onClose}>
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Divider />
            <div style={{ height: 4 }}>
              {loading && sortedNodes.length === 0 && (
                <LinearProgress variant="query" />
              )}
            </div>
            {noMatches ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: theme.spacing(3),
                  height: 200,
                }}
              >
                <Typography>No Matches</Typography>
              </Box>
            ) : sortedNodes.length > 0 ? (
              <Virtuoso
                data={sortedNodes}
                itemContent={(index, node) => (
                  <SearchableNode key={node.id} node={node} onClose={onClose} />
                )}
                style={{ height: 460 }}
                endReached={loadMore}
                components={{
                  Footer: () =>
                    isLoadingMore ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: theme.spacing(2),
                        }}
                      >
                        <LinearProgress sx={{ width: '100%' }} />
                      </Box>
                    ) : null,
                }}
              />
            ) : null}
          </Paper>
        </ClickAwayListener>
      </Popover>
    </Backdrop>
  );
};

export default AdminMultiSearch;
